import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  IconButton,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BatchPredictionIcon from "@mui/icons-material/BatchPrediction";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HistoryIcon from "@mui/icons-material/History";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Logo from "../assets/Images/prodseal-logo.svg";
import Lockup from "../assets/Images/prodseal-lockup-a.svg";
import CustomListItemButton from "./CustomListItemButton";
import { SidebarItem, SidebarProps } from "../types/components/sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useAppSelector } from "../store/hooks";

const BASE_SIDEBAR_ITEMS: SidebarItem[] = [
  { type: "header", primaryText: "Home" },
  {
    type: "item",
    primaryText: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
  },
  { type: "header", primaryText: "Apps" },
  {
    type: "item",
    primaryText: "Products",
    icon: <Inventory2OutlinedIcon />,
    path: "/products",
  },
  {
    type: "item",
    primaryText: "Batches",
    icon: <BatchPredictionIcon />,
    path: "/batches",
  },
  {
    type: "item",
    primaryText: "Scan History",
    icon: <HistoryIcon />,
    path: "/users/scan-history",
  },
  { type: "header", primaryText: "Engagement" },
  {
    type: "item",
    primaryText: "Customers",
    icon: <FaceRetouchingNaturalIcon />,
    path: "/customer",
  },
  {
    type: "item",
    primaryText: "Counterfeit",
    icon: <SummarizeIcon />,
    path: "/users/scan-history?possible_counterfeit=true",
  },
  { type: "header", primaryText: "Others" },
  {
    type: "item",
    primaryText: "Users",
    icon: <PeopleOutlinedIcon />,
    path: "/users",
  },
  {
    type: "item",
    primaryText: "Settings",
    icon: <SettingsOutlinedIcon />,
    path: "/settings",
  },
];

const BRANDS_ITEMS: SidebarItem[] = [
  { type: "header", primaryText: "Brands" },
  {
    type: "item",
    primaryText: "All Brands",
    icon: <StorefrontIcon />,
    path: "/brands",
  },
];

const Sidebar = ({ isCollapsed, onClick }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fullPath = location.pathname + (location.search || "");
  const [selectedPath, setSelectedPath] = useState<string>(fullPath);
  const profile = useAppSelector((state) => state.users.profile);

  const sidebarItems: SidebarItem[] = profile?.is_superuser
    ? [...BASE_SIDEBAR_ITEMS, ...BRANDS_ITEMS]
    : BASE_SIDEBAR_ITEMS;

  useEffect(() => {
    setSelectedPath(fullPath);
  }, [fullPath]);

  const handleItemClick = (path?: string) => {
    if (path) {
      setSelectedPath(path);
      navigate(path);
    }
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? 70 : 170,
        flexShrink: 0,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease-in-out",
        height: "calc(100vh - 32px)",
        margin: 2,
        borderRadius: 3,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
        marginRight: 0,
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          minHeight:30
        }}
      >
        {isCollapsed ? (
          <Box
            component="img"
            src={Logo}
            alt="Prodseal"
            sx={{ width: "32px", height: "auto" }}
          />
        ) : (
          <Box
            component="img"
            src={Lockup}
            alt="Prodseal"
            sx={{ height: "32px", width: "auto", ml:"13px" }}
          />
        )}
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List
          component="nav"
          sx={{
            padding: isCollapsed ? "8px 2px" : "4px",
            "& .MuiListItemButton-root": {
              minHeight: 36,
              height: 36,
              justifyContent: "flex-start",
              alignItems: "center",
              textAlign: "left",
              px: 3,
              borderRadius: "8px",
              mb: 0.5,
            },
            "& .MuiListItemIcon-root": {
              minWidth: 36,
              justifyContent: "flex-start",
            },

            "& .MuiListItemText-root": {
              opacity: isCollapsed ? 0 : 1,
              transition: "opacity 0.2s ease-in-out",
              whiteSpace: "nowrap",
            },
          }}
        >
          {sidebarItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.type === "header" && !isCollapsed && (
                <Box mb={0.5}>
                  <Typography
                    variant="h6"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      my: 0.5,
                      fontSize: "0.95rem",
                      opacity: isCollapsed ? 0 : 1,
                      transition: "opacity 0.2s ease-in-out",
                    }}
                  >
                    {item.primaryText}
                  </Typography>
                  <Divider />
                </Box>
              )}

              {item.type === "item" && (
                <ListItem disablePadding sx={{ display: "block" }}>
                  <CustomListItemButton
                    primaryText={item.primaryText}
                    icon={item.icon}
                    onClick={() => handleItemClick(item.path)}
                    selected={selectedPath === item.path}
                    sx={{
                      minHeight: 48,
                      justifyContent: "flex-start",
                      px: 2,
                      borderRadius: "8px",
                      mb: 0.5,
                    }}
                  />
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          margin: 2,
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)", marginLeft: 0 }}
          onClick={onClick}
        >
          {isCollapsed ? (
            < ArrowForwardIosIcon fontSize="small" sx={{ paddingLeft: 0.5 }} />
          ) : (
            < ArrowBackIosIcon fontSize="small" sx={{ padding: 0.25 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default Sidebar;
